import { getTextStyle, isTextStyleModified } from 'editor/src/store/design/util/textStyles';
import { useSelector } from 'editor/src/store/hooks';

/** `usage`: texts following the style; `modified`: those that no longer look like it. */
export type TextStyleUsage = Record<string, { usage: number; modified: number }>;

function isSameUsage(a: TextStyleUsage, b: TextStyleUsage) {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => key in b && a[key].usage === b[key].usage && a[key].modified === b[key].modified)
  );
}

/** GAPI-61895: per style, how many texts follow it and how many of those were edited away from it. */
function useTextStyleUsage(): TextStyleUsage {
  return useSelector((state) => {
    // No prototype: a design is data, and a crafted id like "__proto__" must stay a plain key.
    const usage: TextStyleUsage = Object.create(null);
    const { designData } = state.design;
    // Nothing to count on a design without styles, so hosts that never use them pay nothing.
    if (!designData?.textStyles?.length) {
      return usage;
    }
    designData.spreads.forEach((spread) =>
      spread.pages.forEach((page) =>
        page.groups.media?.forEach((element) => {
          const id = element.type === 'text' ? element.extra.textStyleId : undefined;
          // Only references to a style the design declares count.
          const style = getTextStyle(designData, id);
          if (element.type !== 'text' || !id || !style) {
            return;
          }
          const entry = usage[id] ?? { usage: 0, modified: 0 };
          entry.usage += 1;
          if (isTextStyleModified(style, element)) {
            entry.modified += 1;
          }
          usage[id] = entry;
        }),
      ),
    );
    return usage;
  }, isSameUsage);
}

export default useTextStyleUsage;
